/**
 * The 44px column at the end of a suppression row.
 *
 * A row the recipient created - an unsubscribe or a complaint - gets no Remove
 * item. That is not a permission check that could be relaxed later: taking such
 * a row off would let the next campaign mail somebody who explicitly refused,
 * and the endpoint refuses it too. A menu item that the API is guaranteed to
 * reject is a promise the software cannot keep, so the menu does not make it.
 */
import { Copy, MoreHorizontal, Trash2 } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../i18n/I18nProvider";
import type { SuppressionRow } from "../../../../lib/marketing";
import { Pop, useClosePops } from "../../../shell/Pop";
import { useToast } from "../../../ui";

export function SuppressionRowMenu({
  row,
  canRemove,
  onRemove,
}: {
  row: SuppressionRow;
  /** False when the operator lacks the right, or the row is the recipient's own. */
  canRemove: boolean;
  onRemove: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const close = useClosePops();

  async function copyValue() {
    close();
    try {
      await navigator.clipboard.writeText(row.value);
      toast(t("common.copied"));
    } catch {
      toast(t("common.copyFailed"));
    }
  }

  return (
    <span className="justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={cn("crm-cb", open && "is-on")}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button type="button" className="crm-mi" onClick={() => void copyValue()}>
          <Copy className="size-4" />
          {t("marketing.suppression.copyValue")}
        </button>

        {canRemove ? (
          <>
            <div className="crm-menu-sep" />
            <button
              type="button"
              className="crm-mi crm-mi-danger"
              onClick={() => {
                close();
                onRemove();
              }}
            >
              <Trash2 className="size-4" />
              {t("marketing.suppression.remove")}
            </button>
          </>
        ) : null}
      </Pop>
    </span>
  );
}
